import { AccountVariable } from '../../account-settings/account-settings-models';
import { RgbaColor } from '../color-picker/color-picker.component';

export default class ColorHelpers {
  public static defaultColorPickerColor = '#E4E7E8ff';

  private static removeHash(hex: string) {
    return hex.charAt(0) === '#' ? hex.slice(1) : hex;
  }

  public static isAccountVariable(colorString: string): boolean {
    if (!colorString) {
      return false;
    }
    return /{[A-Za-z][0-9A-Za-z_ ]+}/.test(colorString);
  }

  public static getTextIfContainsAccountVariable(text: string, accountTexts: Array<AccountVariable>) {
    if (!text) {
      return text;
    }
    if (ColorHelpers.isAccountVariable(text)) {
      if (text === '{product_recommendation_default_image}') {
        return '/assets/img/campaign/preview_no_image.png';
      }
      const rx = /{[A-Za-z][0-9A-Za-z_ ]+}/g;
      const resultTexts = text.match(rx);
      if (resultTexts && resultTexts.length > 0) {
        let result = text + '';
        for (let i = 0; i < resultTexts.length; i++) {
          const index = resultTexts.indexOf(resultTexts[i]);
          const variableKey = resultTexts[i].substring(1, resultTexts[i].length - 1);
          const foundVariable = accountTexts.find((t) => t.key.toLowerCase() === variableKey.toLowerCase());
          if (index !== -1 && foundVariable) {
            result = result.replace(resultTexts[i], foundVariable.value);
          }
        }
        return result;
      }
    }
    return text;
  }

  public static getColorIfAccountVariable(color: string, accountColors: Array<AccountVariable>) {
    if (ColorHelpers.isAccountVariable(color)) {
      const foundColor = ColorHelpers.getColorFromAccountVariable(color, accountColors);
      return foundColor ? foundColor : this.defaultColorPickerColor;
    } else {
      return color;
    }
  }

  public static getColorFromAccountVariable(colorString: string, accountColors: Array<AccountVariable>): string {
    if (!accountColors) {
      return undefined;
    }
    const trimmedString = colorString.substr(1, colorString.length - 2);
    const foundVariable = accountColors.find((c) => c.key === trimmedString);
    if (!foundVariable) {
      return undefined;
    }
    return foundVariable.value;
  }

  public static isColorBright(hexColor: string): boolean {
    const { r, g, b } = this.convertHexToDecimals(hexColor);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness >= 220;
  }

  public static convertHexToDecimals(hex: string): {
    r: number;
    g: number;
    b: number;
    a: number;
  } {
    const hashlessHex = this.removeHash(hex);
    const hexObject = this.parseHex(hashlessHex);
    return this.hexesToDecimals(hexObject);
  }

  private static parseHex = (nakedHex) => {
    const isShort = nakedHex.length === 3 || nakedHex.length === 4;

    const twoDigitHexR = isShort ? `${nakedHex.slice(0, 1)}${nakedHex.slice(0, 1)}` : nakedHex.slice(0, 2);
    const twoDigitHexG = isShort ? `${nakedHex.slice(1, 2)}${nakedHex.slice(1, 2)}` : nakedHex.slice(2, 4);
    const twoDigitHexB = isShort ? `${nakedHex.slice(2, 3)}${nakedHex.slice(2, 3)}` : nakedHex.slice(4, 6);
    const twoDigitHexA = (isShort ? `${nakedHex.slice(3, 4)}${nakedHex.slice(3, 4)}` : nakedHex.slice(6, 8)) || 'ff';

    // const numericA = +((parseInt(a, 16) / 255).toFixed(2));

    return {
      r: twoDigitHexR,
      g: twoDigitHexG,
      b: twoDigitHexB,
      a: twoDigitHexA,
    };
  };

  private static hexToDecimal(hex) {
    return parseInt(hex, 16);
  }

  private static hexesToDecimals({ r, g, b, a }) {
    return {
      r: this.hexToDecimal(r),
      g: this.hexToDecimal(g),
      b: this.hexToDecimal(b),
      a: +(this.hexToDecimal(a) / 255).toFixed(2),
    };
  }

  private static isNumeric(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  private static formatRgb(decimalObject, parameterA) {
    const { r, g, b, a: parsedA } = decimalObject;
    const a = this.isNumeric(parameterA) ? parameterA : parsedA;

    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }

  /**
   * Turns an old-fashioned css hex color value into a rgb color value.
   *
   * If you specify an alpha value, you'll get a rgba() value instead.
   *
   * @param The hex value to convert. ('123456'. '#123456', ''123', '#123')
   * @param An alpha value to apply. (optional) ('0.5', '0.25')
   * @return An rgb or rgba value. ('rgb(11, 22, 33)'. 'rgba(11, 22, 33, 0.5)')
   */
  public static hexToRgba(hex: string, a?): string {
    const hashlessHex = this.removeHash(hex);
    const hexObject = this.parseHex(hashlessHex);
    const decimalObject = this.hexesToDecimals(hexObject);

    return this.formatRgb(decimalObject, a);
  }

  public static rgbaColorToHex(rgbaColor: RgbaColor) {
    if (!rgbaColor) {
      return undefined;
    }
    return ColorHelpers.rgbaNumbersToHex(rgbaColor.r, rgbaColor.g, rgbaColor.b, rgbaColor.a);
  }

  public static rgbaNumbersToHex(r: number, g: number, b: number, a: number) {
    return ColorHelpers.rgbaToHex(`rgba(${r},${g},${b},${a})`);
  }

  public static rgbaStringToNumbers(rgbaString: string): {
    r: number;
    g: number;
    b: number;
    a: number;
  } {
    const trimmedRgba = rgbaString.replace(/\s/g, '');
    const rgbaArray = trimmedRgba.substr(5).split(')')[0].split(',');
    return {
      r: Number(rgbaArray[0]),
      g: Number(rgbaArray[1]),
      b: Number(rgbaArray[2]),
      a: rgbaArray.length > 3 ? Number(rgbaArray[3]) : 1,
    };
  }

  public static rgbaToHex(rgba: string) {
    // Turn "rgb(r,g,b)" into [r,g,b]
    const trimmedRgba = rgba.replace(/\s/g, '');
    const rgbaArray = trimmedRgba.substr(5).split(')')[0].split(',');

    let r = (+rgbaArray[0]).toString(16),
      g = (+rgbaArray[1]).toString(16),
      b = (+rgbaArray[2]).toString(16),
      a = Math.round(+rgbaArray[3] * 255).toString(16);

    if (r.length === 1) {
      r = '0' + r;
    }
    if (g.length === 1) {
      g = '0' + g;
    }
    if (b.length === 1) {
      b = '0' + b;
    }
    if (a.length === 1) {
      a = '0' + a;
    }

    return '#' + r + g + b + a;
  }

  public static rgbToHex(rgba: string) {
    const trimmedRgba = rgba.replace(/\s/g, '');
    const rgbaArray = trimmedRgba.substr(5).split(')')[0].split(',');

    let r = (+rgbaArray[0]).toString(16),
      g = (+rgbaArray[1]).toString(16),
      b = (+rgbaArray[2]).toString(16);

    if (r.length === 1) {
      r = '0' + r;
    }
    if (g.length === 1) {
      g = '0' + g;
    }
    if (b.length === 1) {
      b = '0' + b;
    }

    return '#' + r + g + b;
  }

  public static validateHexColor(code: string) {
    const hexCodeRegex = RegExp('^#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6})$');
    return hexCodeRegex.test(code);
  }

  public static validateRgbaColor(color: string) {
    if (!color) {
      return false;
    }
    const toCheck = color.replace(/\s/g, '').toLowerCase();
    const startsWithRgba = toCheck.indexOf('rgba(') === 0;
    if (!startsWithRgba) {
      return false;
    }
    if (toCheck.indexOf(')') !== toCheck.length - 1) {
      return false;
    }
    const valuePart = toCheck.toLowerCase().replace('rgba(', '').replace(')', '');
    const valueParts = valuePart.split(',');
    if (valueParts.length !== 4) {
      return false;
    }
    for (let i = 0; i < 3; i++) {
      const part = Number(valueParts[i]);
      if (isNaN(part) || part < 0 || part > 255) {
        return false;
      }
    }
    const alpha = Number(valueParts[3]);
    if (isNaN(alpha) || alpha < 0 || alpha > 1) {
      return false;
    }
    return true;
  }

  public static getNextDefaultColorName(accountColors) {
    if (!accountColors || accountColors.length === 0) {
      return 'Color 1';
    } else {
      let highestNumber = 0;
      accountColors.forEach((color) => {
        if (color.key.indexOf('Color ') === 0) {
          const rest = color.key.substr(6);
          const restAsNumber = Number(rest);
          if (restAsNumber > highestNumber) {
            highestNumber = restAsNumber;
          }
        }
      });
      return 'Color ' + (highestNumber + 1);
    }
  }

  public static getTransparentColor(hex, alpha) {
    // Remove "alpha" suffix if present
    hex = hex.replace(/^#/, '').slice(0, 6);

    // Ensure the input is a valid hex color
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hex.length !== 6) throw new Error('Invalid hex color: ' + hex);

    // Convert alpha (0-1) to hexadecimal (00-FF)
    const alphaHex = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0');

    // Return the hex color with the alpha channel
    return `#${hex}${alphaHex}`;
  }

  // Mixes the color with white, so the result stays opaque instead of showing what is behind it.
  // weight is the share of the original color (0-1).
  public static getLighterColor(hex: string, weight: number): string {
    if (!hex || ColorHelpers.isColorGradient(hex)) return hex;
    const { r, g, b } = ColorHelpers.convertHexToDecimals(hex);
    const mix = (channel: number) =>
      Math.round(channel * weight + 255 * (1 - weight))
        .toString(16)
        .padStart(2, '0');
    return `#${mix(r)}${mix(g)}${mix(b)}`;
  }

  public static hexToHsl(hex) {
    // Remove the hash if present
    hex = hex.replace(/^#/, '');

    // Expand shorthand form (e.g. "03F") to full form ("0033FF")
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    return {
      h: h * 360, // Hue in degrees [0,360)
      s: s * 100, // Saturation in %
      l: l * 100, // Lightness in %
    };
  }

  public static excludeDarkAndLightColors(hexColors) {
    return hexColors.filter((hex) => {
      // Exclude pure white
      if (hex.toLowerCase() === '#ffffff') return false;
      const { h, s, l } = ColorHelpers.hexToHsl(hex);
      // Exclude grayscale (low saturation)
      if (s < 20) return false;
      // Exclude very dark or very light colors
      if (l < 20 || l > 80) return false;
      // Exclude pastel (low saturation AND high lightness)
      if (s < 40 && l > 70) return false;
      // Passes all filters
      return true;
    });
  }

  public static getContrastColor(backgroundColor: string, target: number = 128): '#000000' | '#ffffff' {
    // Remove the hash symbol if present
    const hex = backgroundColor.replace(/^#/, '');

    // Parse r, g, b values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate brightness (YIQ formula)
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    // Return "black" for light backgrounds, "white" for dark
    return yiq >= target ? '#000000' : '#ffffff';
  }

  static rgbaStringToHex(rgba: string): string {
    const nums = rgba.replace(/\s/g, '').match(/[\d.]+/g);
    if (!nums || nums.length < 3) return null;
    const r = parseInt(nums[0], 10);
    const g = parseInt(nums[1], 10);
    const b = parseInt(nums[2], 10);
    const a = nums.length > 3 ? Math.round(parseFloat(nums[3]) * 255) : 255;
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
  }

  public static lerpHexColor(startHex: string, endHex: string, t: number): string {
    const s = ColorHelpers.convertHexToDecimals(startHex);
    const e = ColorHelpers.convertHexToDecimals(endHex);
    // When a color is fully transparent, use the other color's RGB to avoid lerping toward black
    const sR = s.a === 0 ? e.r : s.r;
    const sG = s.a === 0 ? e.g : s.g;
    const sB = s.a === 0 ? e.b : s.b;
    const eR = e.a === 0 ? s.r : e.r;
    const eG = e.a === 0 ? s.g : e.g;
    const eB = e.a === 0 ? s.b : e.b;
    const r = Math.round(sR + (eR - sR) * t);
    const g = Math.round(sG + (eG - sG) * t);
    const b = Math.round(sB + (eB - sB) * t);
    const a = Math.round((s.a + (e.a - s.a) * t) * 255);
    const hex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}${hex(a)}`;
  }

  public static isColorGradient(color: string | undefined): boolean {
    return color ? color.startsWith('linear-gradient') || color.startsWith('radial-gradient') : false;
  }

  public static isMaterialIconUrl(iconUrl: string): boolean {
    return !!iconUrl?.startsWith('$icon/material/');
  }

  public static getMaterialIconUrlColor(iconUrl: string): string | undefined {
    if (!iconUrl?.includes('?')) return undefined;
    const parts = iconUrl.split('?');
    const varVal = parts[1].replace('color=', '');
    return varVal.includes('{') ? varVal : parts[1].replace('color=', '#');
  }

  public static setMaterialIconUrlColor(iconUrl: string, color?: string): string {
    if (!iconUrl) return iconUrl;
    if (!color) return iconUrl.split('?')[0];
    const match = iconUrl.match(/\$icon\/material\/([^?]+)/);
    if (!match) return iconUrl;
    return `$icon/material/${match[1]}?color=${color.replace('#', '')}`;
  }
}
